import React from 'react';

/* 8 devotional colour themes */
const THEMES = [
  { grad: ['#7C2D12','#C2410C','#FB923C'], pat: '#FEF3C7', glow: '#F97316' }, // Saffron Fire
  { grad: ['#4C1D95','#6D28D9','#A78BFA'], pat: '#FDE68A', glow: '#7C3AED' }, // Sacred Violet
  { grad: ['#881337','#BE123C','#FB7185'], pat: '#FEE2E2', glow: '#E11D48' }, // Lotus Red
  { grad: ['#1E3A5F','#1D4ED8','#60A5FA'], pat: '#FEF9C3', glow: '#3B82F6' }, // Divine Blue
  { grad: ['#14532D','#15803D','#4ADE80'], pat: '#ECFDF5', glow: '#10B981' }, // Ashram Green
  { grad: ['#78350F','#B45309','#FCD34D'], pat: '#FEF9C3', glow: '#D97706' }, // Temple Gold
  { grad: ['#312E81','#4338CA','#818CF8'], pat: '#FCD34D', glow: '#6366F1' }, // Indigo Night
  { grad: ['#701A75','#A21CAF','#E879F9'], pat: '#FEF9C3', glow: '#C026D3' }, // Cosmic Pink
];

/* Deterministic theme from video id */
export function getThemeIndex(id = '') {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (Math.imul(31, h) + id.charCodeAt(i)) | 0;
  return Math.abs(h) % THEMES.length;
}

/* Mandala SVG overlay */
function Mandala({ color }) {
  const ticks24 = Array.from({ length: 24 }, (_, i) => {
    const a = (i * 15 * Math.PI) / 180;
    return {
      x1: 100 + 87 * Math.sin(a), y1: 100 - 87 * Math.cos(a),
      x2: 100 + 93 * Math.sin(a), y2: 100 - 93 * Math.cos(a),
    };
  });
  const petals12 = Array.from({ length: 12 }, (_, i) => {
    const a = (i * 30 * Math.PI) / 180;
    return { cx: 100 + 60 * Math.sin(a), cy: 100 - 60 * Math.cos(a), rot: i * 30 };
  });
  const petals8 = Array.from({ length: 8 }, (_, i) => {
    const a = (i * 45 * Math.PI) / 180;
    return { cx: 100 + 33 * Math.sin(a), cy: 100 - 33 * Math.cos(a), rot: i * 45 };
  });
  const dots8 = Array.from({ length: 8 }, (_, i) => {
    const a = ((i * 45 + 22.5) * Math.PI) / 180;
    return { cx: 100 + 46 * Math.sin(a), cy: 100 - 46 * Math.cos(a) };
  });

  return (
    <svg
      viewBox="0 0 200 200"
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
    >
      {/* Outer rings */}
      <circle cx="100" cy="100" r="95" fill="none" stroke={color} strokeWidth="0.8" opacity="0.22" />
      <circle cx="100" cy="100" r="82" fill="none" stroke={color} strokeWidth="0.4" opacity="0.14" />

      {/* 24 tick marks */}
      {ticks24.map((t, i) => (
        <line key={i} x1={t.x1} y1={t.y1} x2={t.x2} y2={t.y2}
          stroke={color} strokeWidth="1.2" opacity="0.28" />
      ))}

      {/* 12 outer lotus petals */}
      <circle cx="100" cy="100" r="70" fill="none" stroke={color} strokeWidth="0.5" opacity="0.18" />
      {petals12.map((p, i) => (
        <ellipse key={i} cx={p.cx} cy={p.cy} rx="6" ry="13"
          transform={`rotate(${p.rot},${p.cx},${p.cy})`}
          fill={color} fillOpacity="0.18"
          stroke={color} strokeWidth="0.4" strokeOpacity="0.22" />
      ))}

      {/* 8 inner lotus petals */}
      <circle cx="100" cy="100" r="45" fill="none" stroke={color} strokeWidth="0.6" opacity="0.22" />
      {petals8.map((p, i) => (
        <ellipse key={i} cx={p.cx} cy={p.cy} rx="5" ry="11"
          transform={`rotate(${p.rot},${p.cx},${p.cy})`}
          fill={color} fillOpacity="0.24"
          stroke={color} strokeWidth="0.5" strokeOpacity="0.28" />
      ))}

      {/* Dots between petals */}
      {dots8.map((d, i) => (
        <circle key={i} cx={d.cx} cy={d.cy} r="1.8" fill={color} opacity="0.32" />
      ))}

      {/* Centre ring */}
      <circle cx="100" cy="100" r="18"
        fill={color} fillOpacity="0.08"
        stroke={color} strokeWidth="0.8" strokeOpacity="0.3" />
    </svg>
  );
}

/* ── Main export ── */
export default function DevotionalThumbnail({ id = '', title = '', showTitle = true, className = '', style = {} }) {
  const t = THEMES[getThemeIndex(id)];

  return (
    <div
      className={className}
      style={{
        position: 'relative',
        overflow: 'hidden',
        width: '100%',
        height: '100%',
        background: `linear-gradient(155deg, ${t.grad[0]} 0%, ${t.grad[1]} 52%, ${t.grad[2]} 100%)`,
        ...style,
      }}
    >
      {/* Mandala */}
      <Mandala color={t.pat} />

      {/* Radial glow */}
      <div style={{
        position: 'absolute', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '50%', height: '50%',
        background: t.glow, borderRadius: '50%',
        filter: 'blur(26px)', opacity: 0.28,
        pointerEvents: 'none',
      }} />

      {/* OM symbol */}
      <div style={{
        position: 'absolute',
        top: showTitle ? '36%' : '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        color: t.pat,
        fontSize: showTitle ? '2.4rem' : '2.8rem',
        lineHeight: 1,
        fontWeight: 700,
        textShadow: `0 0 28px ${t.glow}, 0 2px 6px rgba(0,0,0,0.45)`,
        zIndex: 2,
        userSelect: 'none',
        letterSpacing: 0,
      }}>
        ॐ
      </div>

      {/* Title overlay */}
      {showTitle && title && (
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          padding: '30px 10px 9px',
          background: 'linear-gradient(to top, rgba(0,0,0,0.78) 0%, transparent 100%)',
          zIndex: 3,
        }}>
          <p style={{
            margin: 0,
            color: '#fff',
            fontSize: '0.78rem',
            fontWeight: 600,
            lineHeight: 1.35,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textShadow: '0 1px 4px rgba(0,0,0,0.55)',
          }}>
            {title}
          </p>
        </div>
      )}
    </div>
  );
}
